/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Constants                                                                            **/
/**************************************************************************************************/
import { Server } from '@/Constants/Server'

/**************************************************************************************************/
/** @import Helpers/HTTP                                                                         **/
/**************************************************************************************************/
import { AxiosHelper } from '@/Helpers/HTTP/AxiosHelper'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @internal Const @name MixinFetch                                                             **/
/**************************************************************************************************/
const MixinFetch = defineComponent(
{
	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		CallEventOnFailedResponse(status: number, path: string) : void
		{
			if (status === null)
			{
				PrintHelper.warning('[MixinFetch] OnServerFailedResponse: Status NULL del Servidor a petición:', path)
				if ('OnServerFailedResponse' in this)
				{
					const self: any = this
					self.OnServerFailedResponse(path)
				}
			}
		},

		CallEventOnCaughtFetchException(path: string, resolution: Server.Fetching.Resolutions, status: number, error: Error) : void
		{
			PrintHelper.warning('[MixinFetch] OnServerCaughtFetchException:', path, resolution, status, error)
			if ('OnServerCaughtFetchException' in this)
			{
				const self: any = this
				self.OnServerCaughtFetchException(path, resolution, status, error)
			}
		},

		async Fetch(method: Server.Fetching.Method, url: string, payload: any = null, headers: any = null) : Promise<FetchResult | undefined>
		{
			// Propiedades obligatorias que deben estar presentes en la data.
			const axiosHelper = new AxiosHelper
			PrintHelper.warning('[MixinFetch]:fetchURL() -> URL:', url)

			if (method === Server.Fetching.Method.GET)
			{
				try
				{
					const response = await axiosHelper.get(url, { headers, params: payload })
					this.CallEventOnFailedResponse(response.status, url)
					return { status: response.status, data: response.data }
				}
				catch (err)
				{
					return this.OnFetchCaughtException(method, url, err)
				}
			}
			else if (method === Server.Fetching.Method.PATCH)
			{
				try
				{
					const response = await axiosHelper.patch(url, { data: payload, headers })
					this.CallEventOnFailedResponse(response.status, url)
					return { status: response.status, data: response.data }
				}
				catch (err)
				{
					return this.OnFetchCaughtException(method, url, err)
				}
			}
			else if (method === Server.Fetching.Method.POST)
			{
				try
				{
					const response = await axiosHelper.post(url, { data: payload, headers })
					this.CallEventOnFailedResponse(response.status, url)
					return { status: response.status, data: response.data }
				}
				catch (err)
				{
					return this.OnFetchCaughtException(method, url, err)
				}
			}
		},

		HandleExceptions(status: number, path: string, error: Error) : void
		{
			// Manejo de posibles excepciones según el status de la petición original.
			this.CallEventOnCaughtFetchException(path, Server.Fetching.Resolutions.NONE, status, error)
		},

		OnFetchCaughtException(action: string, path: string, error: any) : FetchResult
		{
			this.PrintCatchException(action, path, error)
			this.HandleExceptions(error.status || null, path, error)
			return { status: error.response.status, data: error.response.data }
		},

		PrintCatchException(action: string, path: string, err: Error) : void
		{
			PrintHelper.warning(`[MixinFetch] Excepción ocurrida en petición ${action}:`, path)
			PrintHelper.warning('[MixinFetch]', err)
		}
	}
})

/**************************************************************************************************/
/** @export Component @name MixinFetch                                                           **/
/**************************************************************************************************/
export default MixinFetch

/**************************************************************************************************/
/** @export Type @name FetchDataResult                                                           **/
/**************************************************************************************************/
export type FetchDataResult<T = any> = { data: Array<T>, totalPages: number }

/**************************************************************************************************/
/** @export Type @name FetchResult                                                             **/
/**************************************************************************************************/
export type FetchResult<T = any> = { data: { body: [FetchDataResult<T>] }, status: number }